import type * as React from 'react'
import * as CheckboxPrimitive from '@rn-primitives/checkbox'
import { View } from 'react-native'
import Icon from '~/components/Icon.tsx'
import { Body } from '~/components/typography/Content'
import { Heading3 } from '~/components/typography/Titles'
import { cn } from '~/lib/utils'

function Checkbox({
  className,
  indicatorClassName,
  iconClassName,
  iconSize = 18,
  variant = 'outline',
  ...props
}: CheckboxPrimitive.RootProps & {
  ref?: React.RefObject<CheckboxPrimitive.RootRef>
  indicatorClassName?: string
  iconClassName?: string
  iconSize?: number
  variant?: 'outline' | 'outline-dark' | 'background'
}) {
  return (
    <CheckboxPrimitive.Root
      aria-selected={props.checked}
      className={cn(
        'group/checkbox web:peer native:rounded size-8 shrink-0 rounded-lg border-2 border-primary transition-all duration-150 web:ring-offset-background web:focus-visible:outline-none web:focus-visible:ring-2 web:focus-visible:ring-ring web:focus-visible:ring-offset-2 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50',
        variant === 'outline' && 'border-primary bg-transparent',
        variant === 'outline-dark' && 'border-secondary bg-transparent',
        variant === 'background' &&
          'native:shadow-card native:shadow-tertiary-foreground border-tertiary-foreground bg-background web:shadow-md web:shadow-tertiary-foreground',
        'aria-selected:border-accent aria-selected:bg-accent',
        className
      )}
      {...props}>
      <CheckboxPrimitive.Indicator
        className={cn('h-full w-full items-center justify-center', indicatorClassName)}>
        <Icon
          name="check"
          size={iconSize}
          className={cn(
            'text-accent group-aria-selected/checkbox:text-accent-foreground',
            iconClassName
          )}
        />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  )
}

function CheckboxButton({
  checked,
  onCheckedChange,
  className,
  textClassName,
  disabled,
  label,
  description,
  footer,
}: {
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  className?: string
  textClassName?: string
  disabled?: boolean
  label?: string
  description?: string
  footer?: React.ReactNode
}) {
  return (
    <CheckboxPrimitive.Root
      checked={checked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
      aria-selected={checked}
      className={cn(
        'group/checkbox-button ring-0! outline-none! focus-visible:outline-none! outline-0! focus-visible:ring-0! focus-visible:ring-offset-0! flex-row items-start gap-4 rounded-xl border-2 p-4 transition-all duration-150 focus-visible:ring-0',
        checked ? 'border-accent bg-accent' : 'border-muted bg-card',
        !disabled && 'hover:scale-[1.02] active:scale-[0.98]',
        disabled && 'opacity-50',
        className
      )}>
      <View
        className={cn(
          'size-8 shrink-0 items-center justify-center rounded-md border-2 border-secondary/50 transition-all duration-150',
          checked && 'border-accent bg-accent'
        )}>
        <Icon
          name="check"
          size={24}
          className={cn(
            'text-accent opacity-0 transition-opacity duration-150 group-aria-selected/checkbox-button:text-accent-foreground',
            checked && 'opacity-100'
          )}
        />
      </View>
      <View className="flex-1 gap-1">
        <Heading3
          className={cn(
            'font-medium group-aria-selected/checkbox-button:text-accent-foreground',
            textClassName
          )}>
          {label}
        </Heading3>
        {description ? (
          <Body
            className={cn(
              'text-secondary group-aria-selected/checkbox-button:text-accent-foreground',
              textClassName
            )}>
            {description}
          </Body>
        ) : null}
        {footer}
      </View>
    </CheckboxPrimitive.Root>
  )
}

export { Checkbox, CheckboxButton }
